import { useNavigate, useParams } from 'react-router-dom'
import { TopBar } from '../../components/fd/ftux/TopBar'
import { FDFTUXType, FTUXType } from '../../types/fd-ftux.types'
import { fdFtuxConfig } from '../../utils/FD/FTUX/fdFTUXConfig'
import { useEffect, useMemo, useState, useRef } from 'react'
import { BottomBar } from '../../components/fd/ftux/BottomBar'
import { AnimatePresence, motion } from 'framer-motion'
import { onReady, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import { usePreRenderScales } from '../../hooks/usePreRenderScales'
import { FTUXContent } from '../../components/fd/ftux/FTUXContent'

export const FDFTUXPage = () => {
  const { ftuxType } = useParams<{ ftuxType: `${FDFTUXType}` }>()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [selectedRadioBox, setSelectedRadioBox] = useState<string | null>(null)
  const [direction, setDirection] = useState<1 | -1>(1)
  const bodyContainerRef = useRef<HTMLDivElement>(null)
  const parentContainerRef = useRef<HTMLDivElement>(null)

  const ftuxList = useMemo(() => {
    return fdFtuxConfig?.[ftuxType ?? 'TRADITIONAL_FDs'] ?? []
  }, [ftuxType])

  const currentFTUX: FTUXType[0] = useMemo(() => {
    if (ftuxList?.length > 0) return ftuxList?.[currentStep]
    return []
  }, [ftuxList, currentStep])

  useEffect(() => {
    if (currentFTUX?.length > 0) {
      const firstText = currentFTUX?.filter((element) => element.type === 'RADIO_BOX')?.[0]?.title
      if (firstText) setSelectedRadioBox(firstText)
    }
  }, [currentFTUX])

  const ScreenName = useMemo(() => {
    if (currentFTUX?.length > 0) {
      const firstText = currentFTUX?.filter((element) => element.type === 'TEXT')?.[0]?.text
      const words = firstText?.split(' ') ?? []
      const firstTwoWords = words.slice(0, Math.min(2, words.length)).join('_')
      return `FD_FTUX_PAGE_${firstTwoWords}`
    }
    return ''
  }, [currentFTUX])

  const handleRadioBoxClick = (radioBoxTitle: string | null) => {
    if (radioBoxTitle) {
      trackEvent({
        eventName: EventNames.ButtonTap,
        payload: {
          ScreenName,
          screenType: ftuxType,
          eventId: radioBoxTitle
        }
      })
    }
    setSelectedRadioBox(radioBoxTitle)
  }

  usePreRenderScales({
    totalSteps: ftuxList?.length ?? 0,
    currentStep,
    parentContainerRef,
    renderContent: (step) => (
      <FTUXContent
        selectedRadioBox={selectedRadioBox}
        handleRadioBoxClick={handleRadioBoxClick}
        content={ftuxList[step]}
        direction={1}
        isPreRender
      />
    )
  })

  useEffect(() => {
    if (ScreenName) trackEvent({ eventName: EventNames.ScreenLoaded, payload: { ScreenName, screenType: ftuxType } })
  }, [ScreenName])

  const handleLeftClick = () => {
    setDirection(-1)
    setCurrentStep((prev) => {
      if (prev > 0) return prev - 1
      else {
        navigate('/fd-ftux-home')
        return prev
      }
    })
  }

  const handleRightClick = () => {
    setDirection(1)
    setCurrentStep((prev) => {
      if (prev < ftuxList.length - 1) return prev + 1
      return prev
    })
  }

  useEffect(() => {
    const handlePageClick = (event: MouseEvent | TouchEvent) => {
      if (!parentContainerRef.current) return

      let targetElement = event.target as HTMLElement
      while (targetElement && targetElement !== parentContainerRef.current) {
        if (
          ['A', 'BUTTON', 'INPUT', 'TEXTAREA', 'SELECT'].includes(targetElement.tagName) ||
          getComputedStyle(targetElement).cursor === 'pointer'
        ) {
          return
        }
        targetElement = targetElement.parentElement as HTMLElement
      }

      const containerWidth = parentContainerRef.current.offsetWidth
      const clickX = 'clientX' in event ? event.clientX : event.touches[0].clientX
      const containerLeft = parentContainerRef.current.getBoundingClientRect().left

      const relativeClickX = clickX - containerLeft

      if (relativeClickX < containerWidth / 2) {
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            eventId: 'back_tapped',
            ScreenName,
            screenType: ftuxType
          }
        })
        handleLeftClick()
      } else {
        trackEvent({
          eventName: EventNames.UserClicked,
          payload: {
            eventId: 'next_tapped',
            ScreenName,
            screenType: ftuxType
          }
        })
        handleRightClick()
      }
    }

    const container = parentContainerRef.current
    if (container) {
      // container.addEventListener('click', handlePageClick)
      container.addEventListener('touchstart', handlePageClick)
    }

    return () => {
      if (container) {
        // container.removeEventListener('click', handlePageClick)
        container.removeEventListener('touchstart', handlePageClick)
      }
    }
  }, [handleLeftClick, handleRightClick, ScreenName, ftuxType])

  const pageVariants = {
    enter: (direction: number) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.1, ease: 'easeInOut' } },
    exit: (direction: number) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      transition: { duration: 0.1, ease: 'easeInOut' }
    })
  }

  useEffect(() => {
    updateAppStatusBar('0767C5', 'light')
    setTimeout(() => {
      onReady()
    }, 100)
  }, [])

  return (
    <div style={{ background: 'linear-gradient(180deg, #0767C5 0%, #119DE3 100%)' }} className='w-full h-screen'>
      <div className="flex flex-col h-full w-full bg-[url('/images/fd/bg-lines.svg')] bg-contain bg-no-repeat bg-blend-soft-light">
        <TopBar ScreenName={ScreenName} screenType={ftuxType} />
        <div
          id='parent-container'
          ref={parentContainerRef}
          className='flex flex-col flex-1 h-1 w-full justify-center items-center pb-24 px-5 relative z-0 text-white overflow-y-visible'
        >
          <div
            id='body-container'
            ref={bodyContainerRef}
            className='relative w-full h-max flex justify-center items-center transition-transform duration-300 ease-in-out'
          >
            <AnimatePresence custom={direction} mode='wait'>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={pageVariants}
                initial='enter'
                animate='center'
                exit='exit'
                className='w-full flex flex-col items-center'
              >
                <FTUXContent
                  selectedRadioBox={selectedRadioBox}
                  handleRadioBoxClick={handleRadioBoxClick}
                  content={currentFTUX}
                  direction={direction}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <BottomBar
          handleRightClick={() => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                eventId: 'next_clicked',
                ScreenName,
                screenType: ftuxType
              }
            })
            handleRightClick()
          }}
          selectedRadioBox={selectedRadioBox}
          isLastStep={currentStep === ftuxList.length - 1}
          ScreenName={ScreenName}
          screenType={ftuxType ?? ''}
        />
      </div>
    </div>
  )
}
